import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Copy, 
  Check, 
  RefreshCw, 
  Loader2, 
  Terminal,
  Wifi,
  WifiOff,
  Clock,
  Cpu
} from 'lucide-react';
import { toast } from 'sonner';
import { formatErrorMessage } from '@/utils/errorHandler';
import { copyToClipboard } from '@/utils/clipboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProvisioningModal = ({ equipment, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [provisioningData, setProvisioningData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (equipment) {
      checkExistingToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipment]);

  const checkExistingToken = async () => {
    setLoading(true);
    setError(null);
    try {
      // Tentar buscar token existente
      const response = await axios.get(`${API}/provisioning/tokens?equipment_id=${equipment.id}`);
      const tokens = response.data;
      
      // Filtrar tokens pendentes (não usados e não expirados)
      const validToken = tokens.find(t => 
        t.status === 'pending' && 
        new Date(t.expires_at) > new Date()
      );
      
      if (validToken) {
        setProvisioningData(validToken);
      }
    } catch (err) {
      // Se não encontrar, está ok - usuário pode gerar novo
      console.log('No existing token found');
    } finally {
      setLoading(false);
    }
  };

  const generateToken = async () => {
    setGenerating(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/provisioning/tokens`, {
        equipment_id: equipment.id,
        expires_in_hours: 24
      });
      setProvisioningData(response.data);
      toast.success('Código de provisionamento gerado!');
    } catch (err) {
      console.error('Error generating token:', err);
      const errorMsg = formatErrorMessage(err, 'Erro ao gerar código');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const copyCommand = async () => {
    if (!provisioningData) return;
    
    const serverUrl = process.env.REACT_APP_BACKEND_URL;
    const command = `curl -sSL "${serverUrl}/api/provisioning/download-agent?code=${provisioningData.claim_code}" | sudo python3 -`;
    
    const success = await copyToClipboard(command);
    if (success) {
      setCopied(true);
      toast.success('Comando copiado!');
      setTimeout(() => setCopied(false), 3000);
    } else {
      toast.info('Selecione o comando manualmente e use Ctrl+C');
    }
  };

  const getExpirationStatus = () => {
    if (!provisioningData) return null;
    
    const expiresAt = new Date(provisioningData.expires_at);
    const now = new Date();
    const diffMs = expiresAt - now;
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMs < 0) {
      return { text: 'Expirado', variant: 'destructive' };
    } else if (diffMins < 10) {
      return { text: `Expira em ${diffMins} min`, variant: 'warning' };
    } else {
      return { text: `Expira em ${diffMins} min`, variant: 'default' };
    }
  };

  const serverUrl = process.env.REACT_APP_BACKEND_URL;
  const curlCommand = provisioningData 
    ? `curl -sSL "${serverUrl}/api/provisioning/download-agent?code=${provisioningData.claim_code}" | sudo python3 -`
    : '';

  const expirationStatus = getExpirationStatus();

  return (
    <Dialog open={!!equipment} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cpu className="w-5 h-5 text-primary" />
            Provisionar Dispositivo - {equipment?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Status do equipamento */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Badge variant={equipment?.status === 'online' ? 'default' : 'secondary'}>
                {equipment?.status === 'online' ? (
                  <><Wifi className="w-3 h-3 mr-1" /> Online</>
                ) : (
                  <><WifiOff className="w-3 h-3 mr-1" /> Offline</>
                )}
              </Badge>
              <span className="text-sm text-muted-foreground">TAG: {equipment?.tag}</span>
            </div>
            {equipment?.provisioning_fingerprint && (
              <Badge variant="outline" className="text-xs">
                Dispositivo vinculado
              </Badge>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : provisioningData ? (
            <>
              {/* Código e expiração */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Código:</span>
                  <code className="px-3 py-1 bg-primary/10 text-primary font-mono text-lg rounded">
                    {provisioningData.claim_code}
                  </code>
                </div>
                {expirationStatus && (
                  <Badge variant={expirationStatus.variant === 'warning' ? 'outline' : expirationStatus.variant}>
                    <Clock className="w-3 h-3 mr-1" />
                    {expirationStatus.text}
                  </Badge>
                )}
              </div>

              {/* Comando curl */}
              <Card className="bg-slate-950 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2 text-slate-400">
                    <Terminal className="w-4 h-4" />
                    <span className="text-xs">Execute no Raspberry Pi:</span>
                  </div>
                  <div className="relative">
                    <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap break-all p-3 bg-slate-900 rounded">
                      {curlCommand}
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 h-8 text-slate-400 hover:text-white"
                      onClick={copyCommand}
                      data-testid="copy-provisioning-command"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Instruções */}
              <div className="text-sm text-muted-foreground space-y-1">
                <p>1. Conecte o Raspberry Pi à rede WiFi</p>
                <p>2. Abra o terminal e execute o comando acima</p>
                <p>3. Aguarde a instalação automática (~2-3 minutos)</p>
                <p>4. O equipamento aparecerá como <Badge variant="default" className="text-xs">Online</Badge> no painel</p>
              </div>

              {/* Botão para gerar novo código */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={generateToken}
                disabled={generating}
                className="w-full"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Gerar Novo Código
              </Button>
            </>
          ) : (
            <>
              {/* Nenhum código existente */}
              <div className="text-center py-6 space-y-4">
                <div className="text-muted-foreground">
                  <p>Nenhum código de provisionamento ativo.</p>
                  <p className="text-sm">Gere um código para configurar o dispositivo IoT.</p>
                </div>
                
                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}

                <Button 
                  onClick={generateToken}
                  disabled={generating}
                  data-testid="generate-provisioning-code"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Cpu className="w-4 h-4 mr-2" />
                  )}
                  Gerar Código de Provisionamento
                </Button>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProvisioningModal;
